// import

import type {PostNode, BlogMainNode} from '@arc/hub-model';

import {styled} from '@linaria/react';
import {useEffect, useState} from 'react';
import {useMedia} from 'react-use';

import {ContentSidebar} from 'modules/App';
import {SignupHeader, SignupFooter} from 'modules/Blog';
import {Image, squareLoader} from 'modules/Media';
import {BannerImage, CandyRule, Header, Meta, useTOC} from 'modules/Solo';
import {TypeCapture} from 'modules/Type';

import {PostAuthor} from './PostAuthor';
import {PostSeo} from './PostSeo';
import {PostSeries} from './PostSeries';
import {RelatedPosts} from './RelatedPosts';

// types

type Props = {
  main: BlogMainNode;
  post: PostNode;
  related?: PostNode[];
};

// hooks

function useDelayedMedia(query: string, def?: boolean) {
  const [state, setState] = useState(false);
  const result = useMedia(query, def);

  useEffect(() => setState(result), [result]);

  return state;
}

// component

export function PostPage(props: Props) {
  const {post, main, related = []} = props;
  const {meta, clip, more} = post;
  const isTall = useDelayedMedia('(min-height: 63em)', false);
  const showHeader = useDelayedMedia('(min-height: 54em)', false);
  const bodyToc = useTOC(meta.headings, {min: 3, max: 50});
  const sideToc = useTOC(meta.headings, {min: 3, max: isTall ? 25 : 15});
  const popupCta = post.meta.popupCtaRef || main.meta.popupCtaRef;
  const footerCta = post.meta.footerCtaRef || main.meta.footerCtaRef;

  return (
    <>
      <PostSeo main={main} post={post} />

      {popupCta && showHeader ? (
        <SignupHeader cta={popupCta} />
      ) : null}

      <ContentSidebar
        stickySidebar
        content={(
          <Content>
            <BannerImage className="figure" node={post} />
            <Header className="header" node={post} />
            <Meta className="meta" node={post} />

            <TypeCapture className="body clip" html={clip} trim="top" />
            <div className="bodyToc">{bodyToc}</div>

            <TypeCapture
              className="body more"
              data-test="article-body"
              html={more}
              trim="end" />

            {footerCta ? (
              <SignupFooter className="cta" cta={footerCta} />
            ) : null}

            {post.meta.seriesRef && !footerCta ? (
              <CandyRule />
            ) : null}

            {post.meta.seriesRef ? (
              <PostSeries post={post} series={post.meta.seriesRef} />
            ) : null}

            {post.meta.authorRef && (post.meta.seriesRef || !footerCta) ? (
              <CandyRule />
            ) : null}

            {post.meta.authorRef ? (
              <PostAuthor person={post.meta.authorRef} />
            ) : null}

            {post.meta.bannerImage ? (
              // TODO: Why is this square bannerImage here and hidden? Maybe social share?
              <div className="square">
                <Image
                  alt={post.meta.title}
                  height={1080}
                  layout="fixed"
                  loader={squareLoader}
                  src={post.meta.bannerImage}
                  width={1080} />
              </div>
            ) : null}
          </Content>
        )}
        footer={related.length ? (
          <RelatedPosts className="related" nodes={related} />
        ) : null}
        showSidebar={!!sideToc}
        sidebar={(
          <div className="sideToc">{sideToc}</div>
        )}
        sidebarMax="21rem" />
    </>
  );
}

// styles

const Content = styled('div')`
.figure {
  margin-bottom: 0.25rem;
}

.meta {
  margin-bottom: 1.5rem;
}

.cta {
  margin-block: 6rem;
}

.shares {
  margin-block: 4.5rem;
  justify-content: center;
}

.bodyToc {
  display: none;
  visibility: hidden;
  margin: 3rem 0;
}

.square {
  overflow: hidden;
  visibility: hidden;
  position: absolute;
  bottom: 0;
  left: -1000rem;
  width: 0;
  height: 0;
}

@media (max-width: 53.99875em) {
  .bodyToc {
    display: block;
    visibility: visible;
  }
}
`;
